import emojiRegex from "emoji-regex";

export const DEFAULT_TWEMOJI_SVG_BASE_URL = "https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg";

const SKIP_RECURSION_KEYS = new Set([
	"authorProperties",
	"classes",
	"conditionalVisible",
	"dataSources",
	"externalResources",
	"functions",
	"history",
	"stylesheet",
	"styles",
	"theme",
	"valueTrigger"
]);

function escapeAttribute(value) {
	return `${value}`
		.replace(/&/g, "&amp;")
		.replace(/"/g, "&quot;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;");
}

export function getTwemojiCodePoint(emoji) {
	return Array.from(emoji)
		.map((char) => char.codePointAt(0).toString(16))
		.filter((codePoint) => codePoint !== "fe0f")
		.join("-");
}

export function getTwemojiSvgUrl(emoji, baseUrl = DEFAULT_TWEMOJI_SVG_BASE_URL) {
	const codePoint = getTwemojiCodePoint(emoji);
	const normalizedBaseUrl = `${baseUrl || DEFAULT_TWEMOJI_SVG_BASE_URL}`.replace(/\/+$/, "");
	return `${normalizedBaseUrl}/${codePoint}.svg`;
}

export function emojiToPrintImage(emoji, options = {}) {
	const src = getTwemojiSvgUrl(emoji, options.baseUrl);
	const alt = escapeAttribute(emoji);
	const className = escapeAttribute(options.className || "ambifi-print-emoji");
	const style = escapeAttribute(options.style || "height:1.1em;width:1.1em;margin:0 .05em;vertical-align:-0.15em;display:inline-block;");

	return `<img class="${className}" alt="${alt}" src="${src}" style="${style}" />`;
}

function replaceEmojiInText(value, options = {}) {
	return value.replace(emojiRegex(), (emoji) => emojiToPrintImage(emoji, options));
}

export function replaceEmojiWithPrintImages(value, options = {}) {
	if (typeof value !== "string" || value === "") {
		return value;
	}

	if (!emojiRegex().test(value)) {
		return value;
	}

	if (!value.includes("<")) {
		return replaceEmojiInText(value, options);
	}

	return value
		.split(/(<[^>]+>)/g)
		.map((part) => part.startsWith("<") && part.endsWith(">") ? part : replaceEmojiInText(part, options))
		.join("");
}

export function shouldReplaceEmojiWithPrintImages(config = {}) {
	if (config && config.disablePrintEmojiImages === true) {
		return false;
	}

	const extension = config && config.docTypeExt ? `${config.docTypeExt}`.toLowerCase() : "pdf";
	return extension === "pdf";
}

export function recurseReplaceEmojiWithPrintImages(value, options = {}) {
	if (typeof value === "string") {
		return replaceEmojiWithPrintImages(value, options);
	}

	if (Array.isArray(value)) {
		for (let i = 0; i < value.length; i++) {
			value[i] = recurseReplaceEmojiWithPrintImages(value[i], options);
		}
		return value;
	}

	if (!value || typeof value !== "object") {
		return value;
	}

	Object.keys(value).forEach((key) => {
		if (SKIP_RECURSION_KEYS.has(key)) {
			return;
		}
		value[key] = recurseReplaceEmojiWithPrintImages(value[key], options);
	});

	return value;
}

export function preparePdfDataEmojiImages(data, config = {}, options = {}) {
	if (!shouldReplaceEmojiWithPrintImages(config)) {
		return data;
	}

	return recurseReplaceEmojiWithPrintImages(data, options);
}
